import { Button } from 'ui/atoms/Button/Button';
import { CompanyVerificationCard } from 'ui/molecules/CompanyVerificationCard/CompanyVerificationCard';
import { FinancialsCreditCard } from 'ui/molecules/FinancialsCreditCard/FinancialsCreditCard';
import { NetworkScreeningCard } from 'ui/molecules/NetworkScreeningCard/NetworkScreeningCard';
import { OwnershipTreeCard } from 'ui/molecules/OwnershipTreeCard/OwnershipTreeCard';
import { PerpetualFeedCard } from 'ui/molecules/PerpetualFeedCard/PerpetualFeedCard';
import type { FeedAlert } from 'ui/molecules/PerpetualFeedCard/PerpetualFeedCard.types';
import { SalesTargetListCard } from 'ui/molecules/SalesTargetListCard/SalesTargetListCard';
import type { CoverageRow } from 'ui/organisms/CoverageSection/CoverageSection.types';
import type { DataFlowChannel } from 'ui/organisms/DataFlowSection/DataFlowSection.types';
import type { SwitcherStep } from 'ui/organisms/StepSwitcherSection/StepSwitcherSection.types';
import type { TestimonialSpotlightItem } from 'ui/organisms/TestimonialsSpotlightSection/TestimonialsSpotlightSection.types';

import type { TestimonialSlideItem } from 'features/home/pages/HomePage/sections/TestimonialsSection/TestimonialsSection';

import styles from './HomePage.module.scss';

type Translate = (key: string) => string;

// ── Use-case tabs (scroll-driven) ──
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 6.3l2.1 2.1L9.5 3.4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const checklist = (items: string[]) => (
  <ul>
    {items.map((item) => (
      <li key={item}>
        <CheckIcon />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const tabCta = (label: string, href: string) => (
  <Button type="blue" size="lg" route={href}>
    {label}
  </Button>
);

const getMonitoringAlerts = (t: Translate): FeedAlert[] => [
  {
    company: 'Northwind Trading Ltd',
    registry: 'Companies House (UK)',
    change: t('Status change'),
    variant: 'status',
    time: '07:18',
    filing: t('Company status update'),
    from: t('Active'),
    to: t('In liquidation'),
    reference: t('Company 08804411 · filed today'),
  },
  {
    company: 'Acme Holdings GmbH',
    registry: 'Handelsregister (DE)',
    change: t('New director'),
    variant: 'director',
    time: '07:42',
    filing: t('Appointment of managing director'),
    from: 'K. Müller',
    to: 'J. Weber',
    reference: 'Handelsregister B · HRB 184 220',
  },
  {
    company: 'Contoso SAS',
    registry: 'RNE (FR)',
    change: t('New ownership'),
    variant: 'ownership',
    time: '08:05',
    filing: t('Shareholder register update'),
    from: 'R. Dubois · 100%',
    to: 'R. Dubois · 60% · Meridian Cap. 40%',
    reference: t('RNE · filed today'),
  },
  {
    company: 'Fabrikam AB',
    registry: 'Bolagsverket (SE)',
    change: t('New filing'),
    variant: 'accounts',
    time: '08:31',
    filing: t('Filed revenue'),
    from: 'FY24 · SEK 412M',
    to: 'FY25 · SEK 468M',
    reference: t('Bolagsverket · filed today'),
  },
  {
    company: 'Ibérica Logística SL',
    registry: 'Registro Mercantil (ES)',
    change: t('Address change'),
    variant: 'address',
    time: '09:03',
    filing: t('Registered office update'),
    from: 'Calle Mayor 12, Madrid',
    to: 'Av. Diagonal 640, Barcelona',
    reference: t('Registro Mercantil · filed today'),
  },
];

export const getUseCaseSteps = (t: Translate): SwitcherStep[] => [
  {
    key: 'kyb',
    index: '01',
    tabTitle: t('KYB & Onboarding'),
    tabSub: t('Verify before you transact'),
    title: t('Verify any business against the source registry, updated daily.'),
    body: (
      <div className={styles.useCaseBody}>
        {checklist([
          t('Instant verification against 400 government registries, synced daily'),
          t('UBO and shareholder checks built for KYB and AML workflows'),
          t('VAT and registration number validation across jurisdictions'),
          t('Ongoing monitoring — get alerted when a filing changes'),
        ])}
        {tabCta(t('See KYB in action'), '/kyb-verification')}
      </div>
    ),
    card: <CompanyVerificationCard />,
  },
  {
    key: 'ubo',
    index: '02',
    tabTitle: t('UBO & Ownership Graph'),
    tabSub: t('See who really owns whom'),
    title: t('Follow the ownership chain. Across every border it crosses.'),
    body: (
      <div className={styles.useCaseBody}>
        {checklist([
          t('Cross-border group structures resolved to the ultimate parent'),
          t('UBO identification at 25%+ control, per filed disclosures'),
          t('Shareholder registers from official registry records'),
          t('Direct and total ownership percentages at every layer'),
        ])}
        {tabCta(t('Map a structure'), '/ubo-discovery')}
      </div>
    ),
    card: <OwnershipTreeCard />,
  },
  {
    key: 'financials',
    index: '03',
    tabTitle: t('Financials & Credit Risk'),
    tabSub: t('Filed statements, worldwide'),
    title: t('Read the filed accounts. Decide the exposure.'),
    body: (
      <div className={styles.useCaseBody}>
        {checklist([
          t('Private and listed company financials, where disclosed — up to 20 years of filing history'),
          t('Statements digitised with OCR and AI — standardised line items, comparable across jurisdictions'),
          t('Credit scores and recommended credit limits, built on the filed financials'),
          t('Trade payment data, court judgments and insolvencies as risk signals'),
          t('Original filed documents included alongside the structured data'),
        ])}
        {tabCta(t('Pull filed financials'), '/company-financials')}
      </div>
    ),
    card: <FinancialsCreditCard />,
  },
  {
    key: 'monitoring',
    index: '04',
    tabTitle: t('Perpetual Monitoring'),
    tabSub: t('Alerted when the registry changes'),
    title: t('Onboarding is a snapshot. The registry keeps moving.'),
    body: (
      <div className={styles.useCaseBody}>
        {checklist([
          t('Alerts when status, directors, ownership or filings change at the source registry'),
          t('Registries synced daily — see changes as they are filed, not at the annual review'),
          t('Monitor an entire portfolio of counterparties across 200+ countries'),
          t('Every alert carries the source registry and the filing that triggered it'),
        ])}
        {tabCta(t('Monitor a portfolio'), '/continuous-monitoring')}
      </div>
    ),
    card: (
      <PerpetualFeedCard
        alerts={getMonitoringAlerts(t)}
        label={t('Portfolio monitor · live')}
        monitoredCount="1,240"
        footerNote={t('Each alert linked to its filing')}
      />
    ),
  },
  {
    key: 'screening',
    index: '05',
    tabTitle: t('PEPs & Sanctions'),
    tabSub: t('Screen the people behind the entity'),
    title: t('Screen the people, the companies and the entire chain — in one click.'),
    body: (
      <div className={styles.useCaseBody}>
        {checklist([
          t('One click screens the company, its directors, its UBOs and every entity up the ownership chain'),
          t('Sanctions, PEP and watchlist checks on every party the registry filings identify'),
          t('Parties are resolved from the filings, not typed in — so no hidden owner or parent goes unscreened'),
          t('Audit-ready results tied to the filing that identified each person or entity'),
        ])}
        {tabCta(t('Screen a company'), '/peps-sanctions')}
      </div>
    ),
    card: <NetworkScreeningCard />,
  },
  {
    key: 'salesintel',
    index: '06',
    tabTitle: t('Sales Intelligence'),
    tabSub: t('Target on filed data, not guesses'),
    title: t('Find the companies that are actually growing.'),
    body: (
      <div className={styles.useCaseBody}>
        {checklist([
          t('Prospect 600M+ companies by industry, size, filed revenue and geography'),
          t(
            'Filter on local classification codes — UK SIC, Italian ATECO, German WZ, NACE — not a flattened global taxonomy',
          ),
          t(
            'Growth signals from the filings: year-on-year revenue and EBITDA growth, plus newly incorporated companies',
          ),
          t('Browser extension surfaces contact data for key executives while you research'),
        ])}
        {tabCta(t('Build a target list'), '/prospecting')}
      </div>
    ),
    card: <SalesTargetListCard />,
  },
];

// ── Coverage CTA ──
export const getCoverageRows = (t: Translate): CoverageRow[] => [
  { label: t('Europe'), value: t('180+ registries') },
  { label: t('Americas'), value: t('90+ registries') },
  { label: t('Asia-Pacific'), value: t('85+ registries') },
  { label: t('Middle East & Africa'), value: t('60+ registries') },
];

// ── Products / channels ──
export const getChannelsData = (t: Translate): DataFlowChannel[] => [
  {
    title: t('Platform'),
    description: t('Search, reports and monitoring for analysts working cases directly.'),
    link: { href: '/platform', label: t('Start searching') },
  },
  {
    title: 'Regis',
    badge: { label: 'AI' },
    description: t(
      'Ask the whole dataset in plain language. Regis answers on registry data and returns the filing behind every number, so the answer can be checked rather than trusted.',
    ),
    links: [
      { href: '/regis', label: t('Try Regis App') },
      {
        href: '/regis-api',
        label: 'Regis API',
      },
    ],
  },
  {
    title: 'API',
    description: t('Instant KYB, financials, ownership and credit endpoints, plus registry data inside any AI agent.'),
    links: [
      { href: 'https://api.globaldatabase.com/docs/v2', label: t('View API Docs') },
      { href: '/mcp-integration', label: t('MCP server') },
    ],
  },
  {
    title: t('Data Feeds'),
    description: t('Bulk datasets, enrichment and redistribution licensing to power your own product.'),
    link: { href: '/book-a-demo', label: t('Talk to data sales') },
  },
  {
    title: t('CRM Integrations'),
    description: t('Enriched records and monitoring inside the systems your teams already work in.'),
    links: [
      {
        href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4',
        label: 'Salesforce',
      },
      {
        href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment',
        label: 'HubSpot',
      },
      { href: '/microsoft-dynamics-integration', label: 'Dynamics' },
    ],
  },
  {
    title: t('AI Connectors'),
    description: t('Registry data inside your AI assistant, under your own access controls.'),
    links: [
      { href: 'https://claude.ai/new#directory/connectors/global-database', label: t('Add to Claude') },
      {
        href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9',
        label: t('Add to ChatGPT'),
      },
    ],
  },
];

// ── Testimonials (consumed by About & Partner pages via TestimonialsSection) ──
export const testimonialsData: TestimonialSlideItem[] = [
  {
    logoSrc: 'img/customers/quantifind.svg',
    logoAlt: 'Quantifind',
    quote:
      "When you're resolving entities and mapping risk across borders, the foundation has to be authoritative and traceable — a registry-sourced record we can stand behind when a decision is questioned. Global Database gives us that across the markets we're expanding into, and their team absorbs the registry-level differences and constant changes, so ours stays focused on the intelligence layer rather than the data plumbing.",
    readMoreHref: '#',
    name: 'Adam Mulliken',
    role: 'CPO, Quantifind',
  },
  {
    logoSrc: 'img/customers/kroll.svg',
    logoAlt: 'Kroll',
    quote:
      "We use Global Database's credit risk data when verifying and onboarding customers. It gives our teams the company and financial information they need to assess risk, complete checks and make decisions with confidence.",
    readMoreHref: '#',
    name: 'Carol Zhang',
    role: 'Associate Vice President, Kroll',
  },
  {
    logoSrc: 'img/customers/trustpair.svg',
    logoAlt: 'Trustpair',
    quote:
      'We use Global Database to enrich our bank account validation and payee checks with business data. It helps us identify the company behind the payment and gives our customers more confidence before they pay.',
    readMoreHref: '#',
    name: 'Alexandre Outman',
    role: 'Lead Product Manager, Trustpair',
  },
  {
    logoSrc: 'img/customers/schneider.svg',
    logoAlt: 'Schneider Electric',
    quote:
      'Global Database helps us build a clear picture of our addressable market across the UK and Ireland. With more than 100 criteria available, we can segment companies precisely and map the right opportunities to each of our teams.',
    readMoreHref: '#',
    name: 'Cameron Trayner',
    role: 'Schneider Electric',
  },
  {
    logoSrc: 'img/customers/yoono.svg',
    logoAlt: 'YOONO',
    quote:
      "Global Database's shareholder data is an important part of our intelligence graph. It helps us connect individuals to the businesses they own or control, revealing relationships that conventional screening can miss and giving our clients a more complete view of who they are dealing with.",
    readMoreHref: '#',
    name: 'Oli Warriner',
    role: 'Chief Technology Officer, YOONO',
  },
  {
    logoSrc: 'img/customers/experian.svg',
    logoAlt: 'Experian',
    quote:
      'Our products depend on authoritative company information across multiple jurisdictions. The government registry data provided by Global Database helps us enrich our offering and deliver reliable business information across key European markets.',
    readMoreHref: '#',
    name: 'Wendy Song',
    role: 'BDC Director, Experian',
  },
];

export const getHomeTestimonials = (t: Translate): TestimonialSpotlightItem[] => [
  {
    key: 'quantifind',
    company: 'Quantifind',
    href: '/precision-across-borders-how-quantifind-and-global-database-strengthen-risk-intelligence-for-global-organizations',
    logoSrc: 'img/customers/quantifind.svg',
    quote: t(
      "When you're resolving entities and mapping risk across borders, the foundation has to be authoritative and traceable — a registry-sourced record we can stand behind when a decision is questioned. Global Database gives us that across the markets we're expanding into, and their team absorbs the registry-level differences and constant changes, so ours stays focused on the intelligence layer rather than the data plumbing.",
    ),
    initials: 'AM',
    color: '#4a5cff',
    name: 'Adam Mulliken',
    role: t('CPO, Quantifind'),
  },
  {
    key: 'kroll',
    company: 'Kroll',
    logoSrc: 'img/customers/kroll.svg',
    quote: t(
      "We use Global Database's credit risk data when verifying and onboarding customers. It gives our teams the company and financial information they need to assess risk, complete checks and make decisions with confidence.",
    ),
    initials: 'CZ',
    color: '#3bb56b',
    name: 'Carol Zhang',
    role: t('Associate Vice President, Kroll'),
  },
  {
    key: 'trustpair',
    company: 'Trustpair',
    href: '/how-trustpair-prevents-b2b-payment-fraud-with-real-time-kyb-verification-from-global-database',
    logoSrc: 'img/customers/trustpair.svg',
    quote: t(
      'We use Global Database to enrich our bank account validation and payee checks with business data. It helps us identify the company behind the payment and gives our customers more confidence before they pay.',
    ),
    initials: 'AO',
    color: '#2b7fd4',
    name: 'Alexandre Outman',
    role: t('Lead Product Manager, Trustpair'),
  },
  {
    key: 'schneider',
    company: 'Schneider Electric',
    href: '/schneider-electric-uses-global-database-to-map-63m-companies-and-never-miss-a-sales-opportunity',
    logoSrc: 'img/customers/schneider.svg',
    quote: t(
      'Global Database helps us build a clear picture of our addressable market across the UK and Ireland. With more than 100 criteria available, we can segment companies precisely and map the right opportunities to each of our teams.',
    ),
    initials: 'CT',
    color: '#ff8a3d',
    name: 'Cameron Trayner',
    role: 'Schneider Electric',
  },
  {
    key: 'yoono',
    company: 'YOONO',
    href: '/case-study-how-yoonoai-uses-global-database-to-verify-business-ownership-during-candidate-screening',
    logoSrc: 'img/customers/yoono.svg',
    quote: t(
      "Global Database's shareholder data is an important part of our intelligence graph. It helps us connect individuals to the businesses they own or control, revealing relationships that conventional screening can miss and giving our clients a more complete view of who they are dealing with.",
    ),
    initials: 'OW',
    color: '#7c3aed',
    name: 'Oli Warriner',
    role: t('Chief Technology Officer, YOONO'),
  },
  {
    key: 'experian',
    company: 'Experian',
    href: '/experian-enhances-benelux-company-data-coverage-with-global-database-partnership',
    logoSrc: 'img/customers/experian.svg',
    quote: t(
      'Our products depend on authoritative company information across multiple jurisdictions. The government registry data provided by Global Database helps us enrich our offering and deliver reliable business information across key European markets.',
    ),
    initials: 'WS',
    color: '#e0455f',
    name: 'Wendy Song',
    role: t('BDC Director, Experian'),
  },
];
